import { useEffect, useState } from "react";
import { authedFetch } from "../../lib/api-client";
import type { ApiEnvelope } from "@a2/core/schemas";
import { useT } from "../../lib/use-t";

type FocusResponse = { tips: Array<{ id: string; message: string }> };

/**
 * Full-width "Tip van de dag" strip. Reuses the /study/focus source (same as
 * StudyTips), shows the first tip's message, and falls back to a static A2
 * coaching line from i18n so the strip never renders empty. Framed style
 * (lightbulb + eyebrow + serif-italic), distinct from the de-framed StudyTips.
 */
export function TipVanDeDag() {
  const t = useT();
  const [tip, setTip] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/study/focus");
        if (!res.ok) {
          if (!cancelled) setTip(t("vandaag.tip.fallback"));
          return;
        }
        const json = (await res.json()) as ApiEnvelope<FocusResponse>;
        if (cancelled) return;
        const first = json.ok ? json.data.tips[0]?.message : undefined;
        setTip(first ?? t("vandaag.tip.fallback"));
      } catch {
        if (!cancelled) setTip(t("vandaag.tip.fallback"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Reserve height while loading so the page doesn't shift on hydrate.
  return (
    <section className="flex items-start gap-4 rounded-[var(--radius-lg)] border border-[var(--border)] bg-gradient-to-b from-[var(--accent-soft)] to-transparent p-[22px] sm:px-6">
      <span
        aria-hidden
        className="grid h-[38px] w-[38px] flex-none place-items-center rounded-full bg-[var(--accent)] text-white"
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7c.6.5 1 1.3 1 2.3h6c0-1 .4-1.8 1-2.3A7 7 0 0012 2z" />
        </svg>
      </span>
      <div className="min-w-0">
        <p className="mb-1.5 font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("vandaag.tip.eyebrow")}
        </p>
        {tip === null ? (
          <div className="h-5 w-3/4 animate-pulse rounded bg-[var(--panel-soft)]/60" aria-hidden />
        ) : (
          <p className="font-serif-italic text-base leading-relaxed text-[var(--ink)] sm:text-lg">
            {tip}
          </p>
        )}
      </div>
    </section>
  );
}
